import { createContext, useContext } from "react";

/** Row density. `small` tightens vertical cell padding, matching MUI's `size="small"`. */
export type TableSize = "medium" | "small";

/**
 * `"table"` lays cells out in columns. `"stacked"` is for narrow containers:
 * the header is hidden and each row's cells stack vertically.
 */
export type TableLayout = "table" | "stacked";

/** Which section a cell sits in. Decides its text style and header semantics. */
export type TableCellVariant = "head" | "body" | "footer";

interface TableContextValue {
  readonly size: TableSize;
}

const TableContext = createContext<TableContextValue>({ size: "medium" });

/** Set by `TableHead`, `TableBody` and `TableFooter`; read by `TableCell`. */
const TableSectionContext = createContext<TableCellVariant>("body");

/** Set by a `TableContainer` with `horizontalScroll`; read by `Table` to size itself. */
const TableScrollContext = createContext(false);

/** Set by `TableContainer`, which measures its width against `stackBelow`. */
const TableLayoutContext = createContext<TableLayout>("table");

export const TableContextProvider = TableContext.Provider;
export const TableScrollProvider = TableScrollContext.Provider;
export const TableSectionProvider = TableSectionContext.Provider;
export const TableLayoutProvider = TableLayoutContext.Provider;

export function useTableContext(): TableContextValue {
  return useContext(TableContext);
}

export function useTableSection(): TableCellVariant {
  return useContext(TableSectionContext);
}

export function useTableHorizontalScroll(): boolean {
  return useContext(TableScrollContext);
}

/**
 * The layout the enclosing `TableContainer` chose. Use it in cells to
 * rearrange content for the stacked layout, such as joining two columns
 * into one line.
 */
export function useTableLayout(): TableLayout {
  return useContext(TableLayoutContext);
}
